/**
 * Hook: useSubscription
 *
 * Reads profile billing columns and exposes the resolved tier,
 * capabilities, and convenience booleans.
 *
 * Doctrine: MONETIZATION_ARCHITECTURE.md
 * - This hook is read-only for tier resolution.
 * - RevenueCat sync: if RC reports a different tier than the DB profile,
 *   this hook updates the profile row directly so webhooks are not required.
 * - It MUST NOT import or reference any competitive engine module.
 */

import { useMemo, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { createLogger } from '@/lib/logger';
import { useAuth } from '@/contexts/AuthContext';
import {
  normalizeTier,
  resolveCapabilities,
  hasAtLeast,
  type Tier,
  type Capabilities,
} from '@/lib/subscription';
import { devTierOverride } from '@/lib/devTierOverride';

const log = createLogger('USESUBSCRIPTION');

export interface UseSubscriptionResult {
  /** Effective tier after expiry check */
  tier: Tier;
  /** Whether the subscription has expired */
  isExpired: boolean;
  /** Raw expiry timestamp (ISO string) or null */
  expiresAt: string | null;
  /** Resolved capability flags */
  capabilities: Capabilities;
  /** Convenience: tier >= pro */
  isProPlus: boolean;
  /** Convenience: tier >= coach_pro */
  isCoachPlus: boolean;
  /** Convenience: tier >= org_pro */
  isOrgPlus: boolean;
}

export function useSubscription(): UseSubscriptionResult {
  const { profile } = useAuth();

  // Dev tier override (reactive)
  const [devTier, setDevTier] = useState<Tier | null>(devTierOverride.get());
  useEffect(() => {
    if (!__DEV__) return;
    return devTierOverride.subscribe(setDevTier);
  }, []);

  const result = useMemo(() => {
    // In dev, override takes precedence
    const tier = (__DEV__ && devTier) ? devTier : normalizeTier(profile);
    const capabilities = resolveCapabilities(tier);

    const rawTier = profile?.subscription_tier ?? 'free';
    const expiresAt = profile?.subscription_expires_at ?? null;
    const isExpired = rawTier !== 'free' && tier === 'free';

    return {
      tier,
      isExpired,
      expiresAt,
      capabilities,
      isProPlus: hasAtLeast(tier, 'pro'),
      isCoachPlus: hasAtLeast(tier, 'coach_pro'),
      isOrgPlus: hasAtLeast(tier, 'org_pro'),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.subscription_tier, profile?.subscription_expires_at, devTier]);

  // DEV-only: log resolved tier once per change
  const prevLogRef = useRef('');
  useEffect(() => {
    if (!__DEV__) return;
    const key = `${result.tier}|${result.expiresAt}|${result.isExpired}`;
    if (key !== prevLogRef.current) {
      prevLogRef.current = key;
      log.info('[Subscription] resolved tier', {
        rawTierFromProfile: profile?.subscription_tier ?? null,
        expiresAt: result.expiresAt,
        isExpired: result.isExpired,
        resolvedTier: result.tier,
      });
    }
  });

  // RevenueCat sync: reconcile DB profile tier with RevenueCat entitlements.
  // This handles the case where sandbox webhooks don't fire reliably.
  // Only runs on native (iOS/Android), not web.
  //
  // Downgrade safety: RC can return transient empty entitlements during SDK
  // initialization. We NEVER downgrade on the first RC fetch. Instead, if RC
  // returns inactive on the first check, we wait 1.5s and retry once. Only if
  // both the initial fetch AND the retry agree that entitlements are inactive
  // do we allow a downgrade write. Upgrades are applied immediately (safe direction).
  const rcSyncedRef = useRef(false);
  const { session, refreshProfile } = useAuth();
  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (!session?.user?.id) return;
    // Only sync once per session mount to avoid hammering RC API
    if (rcSyncedRef.current) return;
    rcSyncedRef.current = true;

    (async () => {
      try {
        const { getRevenueCatTier } = await import('@/lib/revenueCat');
        const dbTier = profile?.subscription_tier ?? 'free';
        const dbIsActive = dbTier !== 'free';

        // First RC fetch
        const rcTier1 = await getRevenueCatTier();
        const rcIsActive1 = rcTier1 !== 'free';

        if (rcIsActive1 && !dbIsActive) {
          // Safe direction: RC says paid, DB says free — upgrade immediately
          log.info('[Subscription] RC→DB sync: upgrading to', rcTier1);
          const { supabase } = await import('@/lib/supabase');
          await supabase
            .from('profiles')
            .update({ subscription_tier: rcTier1 })
            .eq('id', session.user.id);
          if (refreshProfile) await refreshProfile(true);
          return;
        }

        if (!rcIsActive1 && dbIsActive && !result.isExpired) {
          // RC inactive on first check — could be transient SDK init state.
          // Wait 1.5s and retry before allowing a downgrade.
          log.info('[Subscription] RC→DB sync: inactive on first check, retrying in 1.5s before downgrade');
          await new Promise<void>((resolve) => setTimeout(resolve, 1500));

          const rcTier2 = await getRevenueCatTier();
          const rcIsActive2 = rcTier2 !== 'free';

          if (rcIsActive2) {
            // RC recovered — transient. Upgrade if needed.
            if (!dbIsActive) {
              log.info('[Subscription] RC→DB sync: recovered after retry, upgrading to', rcTier2);
              const { supabase } = await import('@/lib/supabase');
              await supabase
                .from('profiles')
                .update({ subscription_tier: rcTier2 })
                .eq('id', session.user.id);
              if (refreshProfile) await refreshProfile(true);
            }
            return;
          }

          // Both attempts returned inactive — safe to downgrade
          log.info('[Subscription] RC→DB sync: downgrading to free (confirmed after retry)', { dbTier, rcTier1, rcTier2 });
          const { supabase } = await import('@/lib/supabase');
          await supabase
            .from('profiles')
            .update({ subscription_tier: 'free' })
            .eq('id', session.user.id);
          if (refreshProfile) await refreshProfile(true);
          return;
        }

        // RC and DB agree — no write needed
      } catch (err) {
        log.warn('[Subscription] RC sync error (non-blocking):', err);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  return result;
}
